import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../context/AuthContext';
import TaskBoard from '../components/TaskBoard';
import { API_URL } from '../api';
import SpecEditor from '../components/SpecEditor';
import { builderAvailability, spineNodeForSession, stageFor } from '../components/projectSpineLifecycle';
import DeliveryEvidencePanel from '../components/DeliveryEvidencePanel';
import './SpecFlow.css';

const SPEC_TYPES = [
  { code: 'BS', name: 'Business', description: 'Business outcome, value, or operating need.' },
  { code: 'AS', name: 'Architecture', description: 'Technical structure, boundaries, or decisions.' },
  { code: 'FS', name: 'Product', description: 'A user-facing capability or product change.' },
  { code: 'AC', name: 'Agent', description: 'An AI agent, its responsibilities, or guardrails.' },
  { code: 'TC', name: 'Tool contract', description: 'An integration, interface, or tool capability.' },
  { code: 'TS', name: 'Test', description: 'Acceptance, quality, or verification coverage.' },
  { code: 'RS', name: 'Runbook', description: 'An operational procedure or response playbook.' },
  { code: 'SP', name: 'Governance', description: 'A rule, policy, or Forge operating standard.' },
];
const SPEC_LABELS = ['AI', 'frontend', 'backend', 'seguridad', 'datos', 'infraestructura'];

export function isReplySubmitShortcut(event) {
  return event.key === 'Enter'
    && (event.metaKey || event.ctrlKey)
    && !event.nativeEvent?.isComposing;
}

export function selectSpecType(classification, specType) {
  return { ...classification, spec_type: specType };
}

export function toggleSpecLabel(classification, label) {
  return {
    ...classification,
    spec_labels: classification.spec_labels.includes(label)
      ? classification.spec_labels.filter((item) => item !== label)
      : [...classification.spec_labels, label],
  };
}

function getMessageText(message) {
  return (message.content || [])
    .map((part) => (typeof part === 'string' ? part : part?.text || ''))
    .filter(Boolean)
    .join('\n\n');
}

function SpineSpecRoute({ node, stage, spineHref, availability, querySuffix }) {
  const isBuilderStage = ['builder_ready', 'in_execution'].includes(stage.key);
  const blocked = isBuilderStage && availability.blocked;
  return (
    <section className="card spine-route-card" aria-label="Ruta FORGE de esta Spec">
      <div className="spine-route-heading">
        <div><span className="classification-step">Ruta FORGE</span><h2>{stage.label}</h2></div>
        <span className={`status-pill ${stage.color}`}>{stage.label}</span>
      </div>
      <p><b>Responsable actual:</b> {stage.role || 'Equipo FORGE'}</p>
      <p>{stage.detail}</p>
      <div className="spine-route-next"><b>Siguiente acción:</b><span>{blocked ? 'Completar las dependencias pendientes' : stage.action}</span><Link className="btn-create" to={blocked || !isBuilderStage ? spineHref : `/execution${querySuffix}&node_id=${node.id}`}>{blocked ? 'Ver bloqueo en Project Spine' : isBuilderStage ? 'Abrir ejecución' : `Abrir gate de ${stage.role}`}</Link></div>
      {blocked && <div className="spine-route-blocker" role="status"><b>Ejecución bloqueada por dependencia{availability.pendingDependencies.length > 1 ? 's' : ''}</b><p>Builder podrá reclamar el paquete cuando se complete{availability.pendingDependencies.length > 1 ? 'n las Specs indicadas' : ' la Spec indicada'}.</p><ul>{availability.pendingDependencies.map(({ node: dependency, stage: dependencyStage }) => <li key={dependency.id}><Link to={`/specs/${dependency.spec_session_id}${querySuffix}`}>{dependency.title} · {dependencyStage.label}</Link><span>{dependencyStage.role}: {dependencyStage.action}</span></li>)}</ul></div>}
      {isBuilderStage && node.execution_packet_markdown && <details className="spine-route-artifact" open><summary>Execution Packet aprobado</summary><pre>{node.execution_packet_markdown}</pre></details>}
      {isBuilderStage && node.test_spec_markdown && <details className="spine-route-artifact"><summary>Test Spec y Runbook</summary><pre>{node.test_spec_markdown}</pre>{node.runbook_module_markdown && <pre>{node.runbook_module_markdown}</pre>}</details>}
      {isBuilderStage && node.delivery_contract && <DeliveryEvidencePanel evidence={node.delivery_evidence} requiredEvidence={node.delivery_contract.required_evidence || ['ci', 'deployment']} />}
      {isBuilderStage && node.delivery_contract && <details className="spine-route-artifact"><summary>Delivery Contract</summary><pre>{JSON.stringify(node.delivery_contract, null, 2)}</pre></details>}
    </section>
  );
}

// Renders one session end to end, switching sections based on
// session.status rather than being a multi-route wizard -- the whole
// point of a single SpecSession is that it's one continuous record, so
// the URL (/specs/:id) never changes as it moves through
// clarifying -> ready -> approved.
function SpecFlow() {
  const { sessionId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const userId = user?.id;

  const [session, setSession] = useState(null);
  const [spineData, setSpineData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  const [drafting, setDrafting] = useState(false);
  const [draftError, setDraftError] = useState(null);

  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState(null);

  const [reviewing, setReviewing] = useState(false);
  const [reviewError, setReviewError] = useState(null);
  const [reviewResult, setReviewResult] = useState(null);
  const [refining, setRefining] = useState(false);
  const [refineError, setRefineError] = useState(null);

  const [rejecting, setRejecting] = useState(false);
  const [rejectError, setRejectError] = useState(null);
  const [classification, setClassification] = useState({ spec_type: '', spec_labels: [] });
  const [confirmingClassification, setConfirmingClassification] = useState(false);
  const [classificationError, setClassificationError] = useState(null);

  const transcriptEndRef = useRef(null);
  const sendingReplyRef = useRef(false);

  const fetchSession = async () => {
    try {
      const res = await fetch(`${API_URL}/api/specs/${sessionId}?user_id=${userId}`, {
        credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) {
        setSession(data);
        setClassification({
          spec_type: data.spec_type || '',
          spec_labels: data.spec_labels || [],
        });
        setError(null);
        const spineRes = await fetch(`${API_URL}/api/project-spine/${data.repository_id}?user_id=${userId}`, {
          credentials: 'include',
        });
        setSpineData(spineRes.ok ? await spineRes.json() : null);
      } else {
        setError(data.detail || 'Failed to load spec session');
      }
    } catch (err) {
      setError('Network error — is the backend running?');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) fetchSession();
    // eslint-disable-next-line
  }, [sessionId, userId]);

  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [session?.transcript?.length]);

  const sendReply = async () => {
    if (!reply.trim() || sending || sendingReplyRef.current) return;

    sendingReplyRef.current = true;
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/specs/${sessionId}/reply?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: reply }),
        credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) {
        setSession(data);
        setReply('');
      } else {
        setError(data.detail || 'Failed to send reply');
      }
    } catch (err) {
      setError('Network error — is the backend running?');
    } finally {
      sendingReplyRef.current = false;
      setSending(false);
    }
  };

  const handleReply = (event) => {
    event.preventDefault();
    sendReply();
  };

  const handleReplyKeyDown = (event) => {
    if (!isReplySubmitShortcut(event)) return;
    event.preventDefault();
    sendReply();
  };

  const handleDraft = async () => {
    setDrafting(true);
    setDraftError(null);
    try {
      const res = await fetch(`${API_URL}/api/specs/${sessionId}/draft?user_id=${userId}`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) {
        setSession(data);
      } else {
        setDraftError(data.detail || 'Failed to generate spec draft');
      }
    } catch (err) {
      setDraftError('Network error — is the backend running?');
    } finally {
      setDrafting(false);
    }
  };

  const handleReview = async () => {
    setReviewing(true);
    setReviewError(null);
    setReviewResult(null);
    try {
      const res = await fetch(`${API_URL}/api/specs/${sessionId}/review-and-fix?user_id=${userId}`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) {
        setSession(data.session);
        setReviewResult(data);
      } else {
        setReviewError(data.detail || 'Failed to review spec');
      }
    } catch (err) {
      setReviewError('Network error — is the backend running?');
    } finally {
      setReviewing(false);
    }
  };

  const handleRefine = async () => {
    // Kept for backward compat but now review-and-fix does everything
    handleReview();
  };

  const handleApprove = async () => {
    setApproving(true);
    setApproveError(null);
    try {
      const res = await fetch(`${API_URL}/api/specs/${sessionId}/approve?user_id=${userId}`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) {
        await fetchSession();
      } else {
        setApproveError(data.detail || 'Failed to approve spec');
      }
    } catch (err) {
      setApproveError('Network error — is the backend running?');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    const reason = window.prompt('Razón para rechazar (opcional):');
    if (reason === null) return; // User cancelled the prompt

    setRejecting(true);
    setRejectError(null);
    try {
      const res = await fetch(`${API_URL}/api/specs/${sessionId}/reject?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reason || null }),
        credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) {
        await fetchSession();
      } else {
        setRejectError(data.detail || 'Failed to reject spec');
      }
    } catch (err) {
      setRejectError('Network error — is the backend running?');
    } finally {
      setRejecting(false);
    }
  };

  const toggleLabel = (label) => {
    setClassification((current) => toggleSpecLabel(current, label));
  };

  const handleConfirmClassification = async () => {
    if (!classification.spec_type) {
      setClassificationError('Choose a primary spec type before continuing.');
      return;
    }
    setConfirmingClassification(true);
    setClassificationError(null);
    try {
      const res = await fetch(`${API_URL}/api/specs/${sessionId}/classification?user_id=${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(classification),
        credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) {
        setSession(data);
        setClassification({ spec_type: data.spec_type, spec_labels: data.spec_labels || [] });
      } else {
        setClassificationError(data.detail || 'Failed to confirm classification.');
      }
    } catch (err) {
      setClassificationError('Network error — is the backend running?');
    } finally {
      setConfirmingClassification(false);
    }
  };

  if (!userId) {
    return (
      <div className="spec-flow-page">
        <div className="empty-state">
          Not authenticated. <Link to="/">Go back and connect with GitHub</Link>.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="spec-flow-page">
        <div className="empty-state">Loading…</div>
      </div>
    );
  }

  if (error && !session) {
    return (
      <div className="spec-flow-page">
        <div className="message error">{error}</div>
      </div>
    );
  }

  const spineNode = spineNodeForSession(spineData, session.id);
  const stage = stageFor({ session, node: spineNode });
  const availability = builderAvailability(spineData, spineNode);
  const context = new URLSearchParams(searchParams);
  context.set('repo_id', String(session.repository_id));
  if (userId) context.set('user_id', String(userId));
  const querySuffix = `?${context.toString()}`;
  const backHref = `/specs${querySuffix}`;
  const spineHref = `/work${querySuffix}&section=design&focus_node=${spineNode?.id}`;

  return (
    <div className="spec-flow-page">
      <div className="spec-flow-header">
        <Link to={backHref} className="back-link">
          ← Todas las Specs
        </Link>
        <h1>{session.title || `Spec session #${session.id}`}</h1>
        {session.spec_number && <span className="spec-number-badge">{session.spec_number}</span>}
      </div>

      {spineNode && spineData?.spine?.effective_brand && <aside className="spec-brand-context" aria-label="Contexto de marca del proyecto">
        {spineData.spine.effective_brand.logo_url && <img src={spineData.spine.effective_brand.logo_url} alt="" />}
        <div><span>Contexto de marca</span><b>{spineData.spine.effective_brand.brand_name || 'Proyecto FORGE'}</b><small>{spineData.spine.effective_brand.brand_subtitle || 'Sin Brand Kit personalizado'}</small></div>
      </aside>}
      {spineNode ? <SpineSpecRoute node={spineNode} stage={stage} spineHref={spineHref} availability={availability} querySuffix={querySuffix} /> : <>
      <section className="card classification-card" aria-labelledby="classification-heading">
        <div className="classification-heading">
          <div>
            <span className="classification-step">Step 1</span>
            <h2 id="classification-heading">Classify this spec</h2>
          </div>
          {!session.classification_confirmed && <span className="classification-status">Review Forge&apos;s proposal</span>}
        </div>
        <p className="classification-hint">
          Choose one primary purpose, then add every area this work affects.
        </p>
        {session.classification_reason && (
          <div className="classification-reason" role="note">
            <span>Why Forge suggested this</span>
            <p>{session.classification_reason}</p>
          </div>
        )}
        {session.classification_confirmed ? (
          <div className="classification-summary">
            <span className="spec-type-badge">
              {session.spec_type} · {SPEC_TYPES.find((type) => type.code === session.spec_type)?.name || 'Primary type'}
            </span>
            {(session.spec_labels || []).map((label) => <span className="spec-label-badge" key={label}>{label}</span>)}
          </div>
        ) : (
          <div className="classification-form">
            <fieldset className="classification-types">
              <legend>Primary type <span>Choose one</span></legend>
              <div className="classification-type-grid" role="radiogroup" aria-label="Primary spec type">
                {SPEC_TYPES.map((type) => (
                  <label
                    key={type.code}
                    className={`classification-type-option ${classification.spec_type === type.code ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="spec-type"
                      value={type.code}
                      checked={classification.spec_type === type.code}
                      onChange={() => setClassification((current) => selectSpecType(current, type.code))}
                    />
                    <span className="classification-type-code">{type.code}</span>
                    <span className="classification-type-copy">
                      <strong>{type.name}</strong>
                      <small>{type.description}</small>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
            <fieldset className="classification-areas">
              <legend>Affected areas <span>Choose all that apply</span></legend>
              <div className="classification-labels">
                {SPEC_LABELS.map((label) => (
                  <label key={label} className="classification-label-option">
                    <input type="checkbox" checked={classification.spec_labels.includes(label)} onChange={() => toggleLabel(label)} />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            {classificationError && <div className="message error">{classificationError}</div>}
            <button type="button" className="btn-create" onClick={handleConfirmClassification} disabled={confirmingClassification}>
              {confirmingClassification ? 'Confirming…' : 'Confirm & start clarification'}
            </button>
          </div>
        )}
      </section>

      {/* ---- Clarification transcript (always visible, it's the history) ---- */}
      <div className="card transcript-card">
        <h2>Clarification</h2>
        <div className="transcript">
          {session.transcript.map((msg, i) => (
            <div key={i} className={`chat-bubble ${msg.role === 'user' ? 'user' : 'agent'}`}>
              <span className="chat-role">{msg.role === 'user' ? 'You' : 'Forge'}</span>
              <div className="chat-markdown">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {getMessageText(msg)}
                </ReactMarkdown>
              </div>
            </div>
          ))}
          <div ref={transcriptEndRef} />
        </div>

        {session.status === 'clarifying' && (
          <form onSubmit={handleReply} className="reply-form">
            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              onKeyDown={handleReplyKeyDown}
              placeholder="Answer the question above…"
              disabled={sending}
              autoFocus
              rows={3}
              aria-label="Answer the clarification question"
            />
            <div className="reply-actions">
              <span className="reply-shortcut" aria-hidden="true">⌘/Ctrl + Enter to send</span>
              <button type="submit" disabled={sending || !reply.trim()}>
                {sending ? 'Sending…' : 'Send'}
              </button>
              <button type="button" className="btn-reject" onClick={handleReject} disabled={rejecting}>
                {rejecting ? 'Rejecting…' : 'Reject'}
              </button>
            </div>
          </form>
        )}

        {session.status === 'ready' && !session.markdown_draft && (
          <div className="ready-banner">
            <div className="ready-copy">
              <strong>Ready to draft</strong>
              <span>Forge has enough information to draft a spec.</span>
              {draftError && <div className="message error">{draftError}</div>}
            </div>
            <div className="ready-actions">
              <button type="button" className="btn-create" onClick={handleDraft} disabled={drafting}>
                {drafting ? 'Drafting…' : 'Draft Spec'}
              </button>
              <button type="button" className="btn-reject" onClick={handleReject} disabled={rejecting}>
                {rejecting ? 'Rejecting…' : 'Reject'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ---- Draft review + approve (once a draft exists and isn't approved yet) ---- */}
      {session.markdown_draft && session.status === 'ready' && (
        <div className="card draft-card">
          <h2>Draft Spec: {session.title}</h2>

          <SpecEditor
            session={session}
            userId={userId}
            onUpdate={(fields) => {
              setSession((prev) => ({ ...prev, ...fields }));
              // Reset review state when user edits — allows re-running review
              if (fields.markdown_draft !== undefined) {
                setReviewResult(null);
              }
            }}
          />

          <div className="review-block">
            <button
              type="button"
              className="btn-review"
              onClick={handleReview}
              disabled={reviewing || reviewResult}
            >
              {reviewing ? 'Reviewing & Fixing…' : reviewResult ? 'Review Complete' : 'Review & Auto-Fix'}
            </button>
            {reviewError && <div className="message error">{reviewError}</div>}
            {reviewResult && (
              <div className={`review-result ${reviewResult.remaining_issues.length === 0 ? 'clean' : 'has-issues'}`}>
                <p className="review-summary">{reviewResult.summary}</p>
                {reviewResult.remaining_issues.length > 0 && (
                  <ul>
                    {reviewResult.remaining_issues.map((issue, i) => (
                      <li key={i}>
                        <strong>{issue.section}:</strong> {issue.issue} — {issue.explanation}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          {approveError && <div className="message error">{approveError}</div>}
          {rejectError && <div className="message error">{rejectError}</div>}

          <div className="approve-actions">
            <button type="button" className="btn-approve" onClick={handleApprove} disabled={approving}>
              {approving ? 'Approving…' : 'Approve & Start Building'}
            </button>
            <button type="button" className="btn-reject" onClick={handleReject} disabled={rejecting}>
              {rejecting ? 'Rejecting…' : 'Reject Spec'}
            </button>
          </div>
          <p className="approve-hint">
            This commits the spec to the repository's default branch, opens a tracking issue, and
            dispatches worker agents to open PRs for each task. Requires Forge's CI gate
            (forge-checks.yml) to already be merged on the default branch.
          </p>
        </div>
      )}

      {/* ---- Rejected: show closed state ---- */}
      {session.status === 'rejected' && (
        <div className="card rejected-card">
          <h2>Spec Rejected</h2>
          <p className="rejected-notice">This spec session was rejected and is no longer active.</p>
        </div>
      )}

      {/* ---- Approved: tracking issue + live task board ---- */}
      {session.status === 'approved' && (
        <div className="card approved-card">
          <div className="approved-header">
            <h2>{session.spec_number}: {session.title}</h2>
            <div className="approved-links">
              <span className="file-path">{session.spec_file_path}</span>
              {session.tracking_issue_url && (
                <a href={session.tracking_issue_url} target="_blank" rel="noopener noreferrer">
                  Tracking issue #{session.tracking_issue_number} →
                </a>
              )}
            </div>
          </div>
          <TaskBoard sessionId={session.id} userId={userId} />
        </div>
      )}
      </>}
    </div>
  );
}

export default SpecFlow;
